import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Download, ArrowUpRight, ArrowDownRight, ArrowLeftRight } from 'lucide-react';
import { useTransactions } from '@/composables/financial/useTransactions';
import { useAccounts } from '@/composables/financial/useAccounts';
import { useExport } from '@/composables/shared/useExport';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { formatDate, formatCurrency } from '@/lib/formatters';
import { DATE_PRESETS, rangeForPreset } from '@/lib/dateRange';
import { useDebounce } from '@/composables/shared/useDebounce';
import type { Transaction } from '@/types/table';

const typeIcons: Record<string, React.ReactNode> = { INCOME: <ArrowUpRight className="h-4 w-4 text-green-600" />, EXPENSE: <ArrowDownRight className="h-4 w-4 text-red-600" />, TRANSFER: <ArrowLeftRight className="h-4 w-4 text-blue-600" /> };

const columns: ColumnDef<Transaction, unknown>[] = [
  { accessorKey: 'transactionDate', header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />, cell: ({ row }) => formatDate(row.original.transactionDate) },
  { accessorKey: 'type', header: 'Type', cell: ({ row }) => <div className="flex items-center gap-1">{typeIcons[row.original.type]}<span>{row.original.type}</span></div> },
  { accessorKey: 'description', header: 'Description', cell: ({ row }) => row.original.description },
  { accessorKey: 'amount', header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />, cell: ({ row }) => <span className={row.original.type === 'INCOME' ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>{formatCurrency(row.original.amount, row.original.currency)}</span> },
  { accessorKey: 'account', header: 'Account', cell: ({ row }) => row.original.account?.name || '-' },
];

export function TransactionListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlAccountId = searchParams.get('accountId') || '';

  const { transactions, meta, isLoading, filters, exportData } = useTransactions();
  const { accounts } = useAccounts();
  const { exportFile, isExporting } = useExport();
  const [search, setSearch] = useState(filters.current.search);
  const debouncedSearch = useDebounce(search, 300);
  if (debouncedSearch !== filters.current.search) filters.set({ search: debouncedSearch });

  const [showExport, setShowExport] = useState(false);
  const [exp, setExp] = useState({ format: 'csv', accountId: '', type: '', dateFrom: '', dateTo: '', minAmount: '', maxAmount: '' });

  useEffect(() => {
    if (urlAccountId && urlAccountId !== filters.current.accountId) {
      filters.set({ accountId: urlAccountId });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlAccountId]);

  const handleAccountFilterChange = (value: string) => {
    const newAccountId = value === 'all' ? '' : value;
    filters.set({ accountId: newAccountId });
    if (newAccountId) {
      setSearchParams({ accountId: newAccountId });
    } else {
      setSearchParams({});
    }
  };

  const handleDatePreset = (preset: string) => {
    if (preset === 'custom') { filters.set({ datePreset: 'custom' }); return; }
    const { dateFrom, dateTo } = rangeForPreset(preset);
    filters.set({ datePreset: preset, dateFrom, dateTo });
  };

  // Seed the export dialog from the filters already applied on the page; if no
  // date is applied, default to the trailing year.
  const openExport = () => {
    const f = filters.current;
    const seed = (f.dateFrom || f.dateTo) ? { dateFrom: f.dateFrom, dateTo: f.dateTo } : rangeForPreset('last_year');
    setExp({
      format: 'csv',
      accountId: f.accountId || '',
      type: f.type || '',
      dateFrom: seed.dateFrom,
      dateTo: seed.dateTo,
      minAmount: f.minAmount || '',
      maxAmount: f.maxAmount || '',
    });
    setShowExport(true);
  };

  const handleExport = () => {
    const params: Record<string, unknown> = { sortBy: 'transactionDate', sortOrder: 'asc' };
    if (exp.accountId) params.accountId = exp.accountId;
    if (exp.type) params.type = exp.type;
    if (exp.dateFrom) params.dateFrom = exp.dateFrom;
    if (exp.dateTo) params.dateTo = exp.dateTo;
    if (exp.minAmount) params.minAmount = exp.minAmount;
    if (exp.maxAmount) params.maxAmount = exp.maxAmount;
    const ext = exp.format === 'xlsx' ? 'xlsx' : exp.format === 'pdf' ? 'pdf' : 'csv';
    const span = [exp.dateFrom, exp.dateTo].filter(Boolean).join('_to_');
    exportFile(() => exportData(exp.format, params), `transactions${span ? '_' + span : ''}.${ext}`);
    setShowExport(false);
  };

  const selectedAccount = accounts.find(a => a.id === filters.current.accountId);
  const pageTitle = selectedAccount ? `Transactions - ${selectedAccount.name}` : 'Transactions';

  return (
    <PageContainer title={pageTitle} description="All income and expense transactions (auto-generated from expenses, payments, and transfers)" actions={
      <Button variant="outline" onClick={openExport} disabled={isExporting}><Download className="mr-2 h-4 w-4" />Export</Button>
    }>
      <DataTableToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search description or reference..." filters={
        <div className="flex flex-wrap items-center gap-2">
          <Select value={filters.current.accountId || 'all'} onValueChange={handleAccountFilterChange}>
            <SelectTrigger className="w-48"><SelectValue placeholder="All Accounts" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Accounts</SelectItem>
              {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.current.type || 'all'} onValueChange={(v) => filters.set({ type: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-36"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="INCOME">Income</SelectItem>
              <SelectItem value="EXPENSE">Expense</SelectItem>
              <SelectItem value="TRANSFER">Transfer</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.current.datePreset} onValueChange={handleDatePreset}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {filters.current.datePreset === 'custom' && (
            <>
              <Input type="date" aria-label="From date" value={filters.current.dateFrom} onChange={(e) => filters.set({ dateFrom: e.target.value })} className="w-40" />
              <Input type="date" aria-label="To date" value={filters.current.dateTo} onChange={(e) => filters.set({ dateTo: e.target.value })} className="w-40" />
            </>
          )}
          <Input type="number" placeholder="Min amount" value={filters.current.minAmount} onChange={(e) => filters.set({ minAmount: e.target.value })} className="w-32" />
          <Input type="number" placeholder="Max amount" value={filters.current.maxAmount} onChange={(e) => filters.set({ maxAmount: e.target.value })} className="w-32" />
        </div>
      } />
      <DataTable columns={columns} data={transactions} isLoading={isLoading} page={meta?.page ?? 1} totalPages={meta?.totalPages ?? 1} total={meta?.total ?? 0} pageSize={filters.current.limit} onPageChange={(p) => filters.set({ page: p })} onPageSizeChange={(s) => filters.set({ limit: s })} />

      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Transactions</DialogTitle>
            <DialogDescription>Downloads a CSV for the selected range and filters. Pre-filled from the filters on the page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Format</Label>
              <Select value={exp.format} onValueChange={(v) => setExp(s => ({ ...s, format: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV (.csv)</SelectItem>
                  <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                  <SelectItem value="pdf">PDF (.pdf)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Account</Label>
              <Select value={exp.accountId || 'all'} onValueChange={(v) => setExp(s => ({ ...s, accountId: v === 'all' ? '' : v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All accounts</SelectItem>
                  {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={exp.type || 'all'} onValueChange={(v) => setExp(s => ({ ...s, type: v === 'all' ? '' : v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="INCOME">Income</SelectItem>
                  <SelectItem value="EXPENSE">Expense</SelectItem>
                  <SelectItem value="TRANSFER">Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>From</Label><Input type="date" value={exp.dateFrom} onChange={(e) => setExp(s => ({ ...s, dateFrom: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>To</Label><Input type="date" value={exp.dateTo} onChange={(e) => setExp(s => ({ ...s, dateTo: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Min amount</Label><Input type="number" placeholder="Any" value={exp.minAmount} onChange={(e) => setExp(s => ({ ...s, minAmount: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Max amount</Label><Input type="number" placeholder="Any" value={exp.maxAmount} onChange={(e) => setExp(s => ({ ...s, maxAmount: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExport(false)}>Cancel</Button>
            <Button onClick={handleExport} disabled={isExporting}><Download className="mr-2 h-4 w-4" />{isExporting ? 'Exporting…' : 'Export'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
